import { Form, FormInstance, Modal, Upload, message } from 'antd';
import type { UploadChangeParam } from 'antd/es/upload';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';
import { ReactComponent as RemoveImg } from 'assets/icons/x-circle-fill.svg';
import CloudArrowUpImage from 'assets/images/CloudArrowUp.svg';
import { useEffect, useState } from 'react';
//fallback
import { PdfView } from 'presentation/components/pdf/pdfview';
import { showConfirm } from 'presentation/redux/features/app/appSlice';
import { useAppDispatch } from 'presentation/redux/hooks';
import { FILE_TYPE } from 'utilities/constants/COMMON';
import getMessage from 'utilities/constants/MESSAGE';
import { commonHelpers } from 'utilities/helpers';
import { ENV } from '../../../../../infrastructure/config/CONSTANTS';

interface props {
    initialImageUrl?: string | undefined;
    form?: FormInstance;
    existedFile?: any;
    imageId?: number;
    initFile?: UploadFile[];
    setIsDisable?: (value: boolean) => void;
}
const STATUS = {
    UPLOADING: 'uploading',
    DONE: 'done',
    REMOVE: 'removed',
    UNDER_CONSTRUCTION: '施工中',
};

const getBase64Preview = (file: RcFile): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });
const UploadImage = ({ form, initFile, setIsDisable }: props) => {
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [initFileList, setInitFileList] = useState<UploadFile[]>([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');
    const [fileType, setFileType] = useState('');
    const dispatch = useAppDispatch();

    const fileTypes = ENV.FILE_TYPE_CU_043_01?.split(',');
    const maxFileSize = ENV.FILE_MAX_SIZE_CU_043_01;
    const maxFileCount = Number(ENV.FILE_MAX_NUMBER_CU_061_01);

    useEffect(() => {
        if (initFile) {
            setFileList(initFile);
            setInitFileList(initFile);
            setIsDisable &&
                setIsDisable(
                    (!form?.getFieldValue('image') || !form.getFieldValue('image').file) &&
                        (!form?.getFieldValue('isEdit') ||
                            form?.getFieldValue('isEdit')?.length === 0),
                );
        }
    }, [initFile]);

    const beforeUpload = (file: RcFile) => {
        const invalidFileType = commonHelpers.validateFileTypes(file, fileTypes);
        if (invalidFileType) {
            message.error(getMessage('M007', invalidFileType));
        }
        const isValidFileSize = commonHelpers.validateFileSize(file, maxFileSize);
        if (!isValidFileSize) {
            message.error(getMessage('M006', maxFileSize + 'MB'));
        }
        //validate file list
        const isExceedMaxFileCount = FileList.length > maxFileCount;
        if (isExceedMaxFileCount) {
            message.error(getMessage('M010', maxFileCount));
        }

        return !invalidFileType && isValidFileSize && !isExceedMaxFileCount;
    };

    useEffect(() => {
        if (setIsDisable) {
            setIsDisable(fileList.length === 0);
        }
    }, [fileList]);

    const dummyRequest = ({ file, onSuccess }: any) => {
        setTimeout(() => {
            onSuccess('ok');
        }, 0);
    };

    const onChange: UploadProps['onChange'] = (info: UploadChangeParam<UploadFile>) => {
        if (info.file.status === STATUS.REMOVE) {
            const deleteProps = commonHelpers.getConfirmProps(
                'N0900',
                {
                    onOk: () => {
                        const uids = form?.getFieldValue('isEdit')
                            ? form?.getFieldValue('isEdit')
                            : [];
                        if (initFileList?.filter((e) => e.uid === info.file.uid).length) {
                            form?.setFieldValue('isEdit', [...uids, info.file.uid]);
                        }
                        setFileList(info.fileList);
                    },
                },
                '削除',
            );
            dispatch(showConfirm(deleteProps));
        } else {
            const invalidFileType = commonHelpers.validateFileTypes(info.file, fileTypes);
            const isValidFileSize = commonHelpers.validateFileSize(info.file, maxFileSize);
            if (!invalidFileType && isValidFileSize) {
                setFileList(info.fileList);
            }
        }
        if (info.file.status === STATUS.UPLOADING) {
            return;
        }
    };

    const onPreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64Preview(file.originFileObj as RcFile);
        }
        if (file.name?.endsWith('.pdf')) {
            setFileType(FILE_TYPE.PDF);
        } else {
            setFileType(FILE_TYPE.IMAGE);
        }
        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
        setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
    };
    const handleCancel = () => setPreviewOpen(false);
    const uploadButton = (
        <section
            style={{
                padding: '53px 26px',
                width: 343,
                border: '1px solid #D0D3D8',
                borderRadius: 4,
                background: '#ffffff',
            }}
        >
            <div className="center-inside" style={{ width: '100%' }}>
                <img alt="" src={CloudArrowUpImage} style={{ margin: 'auto' }} />
            </div>
            <div
                className="upload-title center-inside"
                style={{ marginTop: 12, color: '#192F51', fontSize: 14, fontWeight: 700 }}
            >
                ファイルをアップロード
            </div>
        </section>
    );
    const removeImage = () => {
        const confirmProps = commonHelpers.getConfirmProps('P013', {
            onOk: () => {
                const isEdit = initFileList.map((item: any) => {
                    return item.uid;
                });
                form?.setFieldValue('isEdit', isEdit);
                setFileList([]);
                setIsDisable && setIsDisable(false);
            },
        });
        dispatch(showConfirm(confirmProps));
    };
    const props: UploadProps = {
        showUploadList: {
            showRemoveIcon: true,
            removeIcon: (
                <RemoveImg
                    fill="red"
                    style={{ background: '#ffffff', borderRadius: '50%' }}
                ></RemoveImg>
            ),
        },
        beforeUpload: beforeUpload,
        onChange: onChange,
        customRequest: dummyRequest,
        onPreview: onPreview,
        fileList: fileList,
        defaultFileList: initFile,
    };
    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ marginBottom: 16 }} className="label">
                    火災保険申込済書<span style={{ color: '#EB5757' }}>*</span>
                </div>
                {fileList.length <= 0 ? (
                    <></>
                ) : (
                    <div className="remove-button" onClick={removeImage}>
                        削除
                    </div>
                )}
            </div>
            <Form.Item name="image" style={{ margin: 0, padding: 0 }}>
                <Upload
                    {...props}
                    className="upload-custom button-style"
                    name="avatar"
                    listType="picture"
                >
                    {uploadButton}
                </Upload>
            </Form.Item>
            <Modal
                open={previewOpen}
                footer={null}
                onCancel={handleCancel}
                width={800}
                style={{
                    maxHeight: 'calc(100vh - 110px)',
                    overflow: 'auto',
                    paddingBottom: 'unset',
                }}
            >
                {fileType == FILE_TYPE.IMAGE && (
                    <img alt="example" style={{ width: '100%' }} src={previewImage} />
                )}
                {fileType == FILE_TYPE.PDF && (
                    <PdfView
                        source={previewImage}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                        width={760}
                    />
                )}
            </Modal>
        </>
    );
};

export default UploadImage;
